import Vue from 'vue';
import { ReferralNotEligibleContentFragment } from '@/models/cms';
import { Component, Watch } from 'vue-property-decorator';
import { calculateAge } from '@/helpers';
import { Routes } from '@/router';
import { EnrollmentOptions } from '@/services/loyalty/loyalty.service';
import { CustomerProfile } from '@/models/customer';
import { CountryCode } from '@/store/ui';
import { RewardsEvent } from '@/services/analytics/events/rewards-event';

@Component({
  name: 'CmgReferralNotEligibleModal',
})
export default class CmgReferralNotEligibleModal extends Vue {
  content: ReferralNotEligibleContentFragment = {};
  
  isActionTaken = false;

  @Watch('visible')
  async onVisible(newValue: boolean) {
      if (newValue) {
        await this.$services.config.setReferralNotEligibleModal();
        this.content = this.$services.config.getReferralNotEligibleModal() || {};
        this.$services.analytics.send(
          new RewardsEvent({
            eventType: 'error',
            error: {
              response: {
                data: {
                  message: this.title,
                },
              },
            },
          }),
        );
      }
  }  

  get customer(): CustomerProfile | null {
    return this.$appStore.state.customer.profile;
  }

  get visible(): boolean {
    return this.$appStore.state.ui.modals.referralNotEligibleModal.visible;
  } 

  get isMobile(): boolean {
    return this.$services.ui.isMobile();
  }

  get title(): string | undefined {
    return this.content.title || `Aw shucks, you're not eligible`;
  }

  get description(): string | undefined {
    return this.content.description || `<p>Looks like you've already joined the Chipotle Rewards Program before, so you're not eligible for a referral.</p><p>Join Chipotle Rewards again to earn points and order your favorites even faster.</p>`;
  }

  get legalCopy(): string | undefined {
    return this.content.legalCopy || `<p>By joining, I am confirming I am ${this.age} years of age or older, will be enrolled in Chipotle Rewards, will receive email updates, promotions and offers from Chipotle, and agree to <a href="https://www.chipotle.com/privacy-policy">Chipotle’s Privacy Policy</a> and <a href="https://www.chipotle.com/rewards-terms">Terms & Conditions</a>.</p>`;
  }

  get age(): string {
    return this.customer?.country === CountryCode.US ? '13' : '16';
  }

  get primaryCtaText(): string {
    return `Join Rewards`;
  }

  async onPrimaryCtaClick() {
    this.isActionTaken = true;

    try {
      const dateOfBirth = this.customer?.dateOfBirth ? new Date(this.customer.dateOfBirth) : undefined;
      const ofAge = (!!dateOfBirth && calculateAge(dateOfBirth) >= 18) || (this.customer?.externallyConfirmed18 ?? false);
      const enrollInEmailMarketing = ofAge && this.customer?.country === CountryCode.US;
      const enrollmentOptions: EnrollmentOptions = {
        customer: this.customer,
        pageTitle: document.title,
        shouldSubscribeToMarketingEmail: enrollInEmailMarketing,
        shouldSubscribeToMarketingSMS: false
      };

      await this.$services.loyalty.enrollInLoyalty(enrollmentOptions);

      await this.$services.customer.setProfileSubscriptionChangeOnly(true);
      await this.$services.customer.refreshCustomerProfileInfo(true, false, true);
      this.$services.ui.setModalVisibility('referralNotEligibleModal', false);

      this.$router.push({ name: Routes.Home });

    } catch (e: any) {
      this.$services.logger.error('Something went wrong while enrolling in loyalty', { error: e });
    } finally {
      this.isActionTaken = false;
    }
  }
  
  onClose() {
    this.$services.ui.setModalVisibility('referralNotEligibleModal', false);
    this.$router.push({ name: Routes.Rewards });
  }
}
